import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LoadingScreen from '../common/LoadingScreen';
import MainLayout from '../layout/MainLayout';
import DailyIntroGate from '../intro/DailyIntroGate';
import OccasionGate from '../occasion/OccasionGate';
import { superAdminMayOpen } from './superAdminRoutes';

// TEST MODE — these accounts get the first-run journey on every full page
// load, whether or not they have already answered it, and answering again
// simply writes another questionnaire row. Empty in normal operation: a new
// person sees it once and never again.
//
// A module-level latch, not state, so moving around inside the app does not
// replay it mid-session; a real reload does.
const FORCE_FIRST_RUN_EMAILS: string[] = [];
let firstRunForcedThisLoad = false;

export function shouldForceFirstRun(email?: string | null): boolean {
  if (firstRunForcedThisLoad) return false;
  if (!email) return false;
  return FORCE_FIRST_RUN_EMAILS.includes(email.toLowerCase());
}

/**
 * Spend the replay. Called when the journey is finished, not when its route
 * renders: signing in briefly mounts the questionnaire route before the login
 * page's own redirect lands, and marking it there burned the replay on a
 * frame the user never saw.
 */
export function markFirstRunReplayed(): void {
  firstRunForcedThisLoad = true;
}

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRoles?: string[];
}

function ProtectedRoute({ children, requiredRoles }: ProtectedRouteProps) {
  const { user, isAuthenticated, isLoading, needsQuestionnaire } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    // Redirect to login page with return url
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Signed in with a temporary password: AuthProvider's change-password
  // dialog is the only thing on screen until it is done. Mounting the app
  // behind it let the dashboard's own pop-ups (the daily check-in, goal and
  // announcement prompts) open on top a moment later — MUI hands keyboard
  // focus to the newest dialog, so the password fields could not be typed
  // in until a refresh, when the once-a-day check-in no longer re-opened.
  if (user?.mustChangePassword) {
    return null;
  }

  // Redirect ALL users to questionnaire if they haven't completed it
  // Skip if user still needs to change password first (password change takes priority)
  const isSuperSuperAdmin = user && user.role === 'super-super-admin';
  const replaying = shouldForceFirstRun(user?.email);
  if ((needsQuestionnaire || replaying) && location.pathname !== '/questionnaire'
      && !isSuperSuperAdmin && !user?.mustChangePassword) {
    return <Navigate to="/questionnaire" state={{ from: location }} replace />;
  }

  // Super-super-admin stays in the MoreMe-wide area: the super admin
  // dashboard, their own profile/settings, and the pages that work across
  // companies (mood analytics, MoreMe courses and the course builder they
  // use for them). Everything else redirects there.
  if (user?.role === 'super-super-admin' && !superAdminMayOpen(location.pathname)) {
    return <Navigate to="/admin/super" replace />;
  }

  // Check role-based access
  if (requiredRoles && requiredRoles.length > 0) {
    if (!user || !requiredRoles.includes(user.role)) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // Wrap protected routes with MainLayout (except questionnaire page)
  if (location.pathname === '/questionnaire') {
    return <>{children}</>;
  }

  // DailyIntroGate holds the whole app behind the once-a-day check-in
  // moment on the first login of the day (fails open on any API error).
  // A birthday plays before the check-in, and the check-in before the app.
  return (
    <OccasionGate>
      <DailyIntroGate>
        <MainLayout>
          {children}
        </MainLayout>
      </DailyIntroGate>
    </OccasionGate>
  );
}

export default ProtectedRoute;
